<script setup lang="ts">
import { ref } from 'vue-demi'
import { useI18n } from 'vue-i18n'
import { computed, inject, Ref } from 'vue'
import { SubjektTypEnum } from '/@src/client-portal'
import { useSteps } from '/@src/composable/useSteps'
import { useUserSession } from '/@src/stores/userSession'
import { NazvyKrokov } from '/@src/utils/zoppm/types'
import {
  VyzvaSpecificationPublicResponse,
  ZoppmCestneVyhlasenie as ZoppmCestneVyhlasenieSpec,
} from '/@src/client-public'
import { ZoppmCestneVyhlasenie as ZoppmCestneVyhlasenieFormDataType } from '/@src/client-portal'
import { poradieComparator } from '/@src/utils/poradieComparator'
import { richTextToGovukHtml } from '/@src/utils/richTextToGovukHtml'
import { useFormValues } from 'vee-validate'
import { ZoppmSchemeTransformer } from '/@src/utils/zoppm'
import { NazvyAssemblySekcii } from '/@src/utils/zoppm/ZoppmSchemeTransformer/types'

export interface CestneVyhlasenie {
  podpisovatelia: any
}

const props = withDefaults(defineProps<CestneVyhlasenie>(), {
  podpisovatelia: undefined,
})

const formValues = useFormValues()

const { t } = useI18n()
const steps = useSteps()
const userSession = useUserSession()
const isFo = ref(
  userSession.user?.subjekt?.typ?.toLowerCase() === SubjektTypEnum.Osoba?.toLowerCase()
)
const specResponse = inject<VyzvaSpecificationPublicResponse>('specResponse')
const stavPodana = inject<boolean>('stavPodana', false)

type ExtendedCestneVyhlasenie = ZoppmCestneVyhlasenieSpec & {
  potvrdeneZiadatelom?: boolean
}

const cestneVyhlasenia = computed<ReadonlyArray<ExtendedCestneVyhlasenie>>(() => {
  if (!specResponse?.zoppmSpec?.cestneVyhlasenia) {
    return []
  }
  const formVyhlasenia = (formValues.value?.cestneVyhlasenia ??
    []) as ReadonlyArray<ZoppmCestneVyhlasenieFormDataType>

  const cestneVyhlasenia: ZoppmCestneVyhlasenieSpec[] = [
    ...specResponse.zoppmSpec.cestneVyhlasenia,
  ]
  cestneVyhlasenia.sort(poradieComparator)

  return cestneVyhlasenia.map((vyhlasenie, index) => ({
    ...vyhlasenie,
    potvrdeneZiadatelom: formVyhlasenia[index]?.potvrdeneZiadatelom ?? false,
  }))
})

const schemeTransformer = inject<Ref<ZoppmSchemeTransformer>>('schemeTransformer')
const sekciaRefNazov: NazvyAssemblySekcii = 'zoppm.ks.vyhlasenie'
const heading = computed(
  () =>
    schemeTransformer?.value.getSectionTitle(sekciaRefNazov) ||
    t('zoppm.zhrnutie.cestneVyhlasenie.mainHeading')
)

const podpisovateliaSekciaRefNazov = isFo.value
  ? 'zoppm.ss.podpisovatelia-fo'
  : 'zoppm.ss.podpisovatelia-po'
const podpisovateliaHeading = computed(
  () =>
    schemeTransformer?.value.getSectionTitle(podpisovateliaSekciaRefNazov) ||
    t('zoppm.zhrnutie.cestneVyhlasenie.osobyZastupujuceSubjekt')
)

const cestneVyhlasenieEnabled = computed(() =>
  schemeTransformer?.value.isSectionEnabled(sekciaRefNazov)
)
</script>

<template>
  <IDSKHeading v-if="cestneVyhlasenieEnabled" as="h2" variant="l" class="mb-7">
    {{ heading }}
  </IDSKHeading>
  <div
    v-if="cestneVyhlasenieEnabled && specResponse?.zoppmSpec?.cestneVyhlasenie"
    class="mb-7"
    v-html="specResponse?.zoppmSpec?.cestneVyhlasenie"
  />

  <template v-if="cestneVyhlasenieEnabled">
    <div
      v-for="cestneVyhlasenie in cestneVyhlasenia"
      :key="cestneVyhlasenie.cestneVyhlasenieSpecId"
      class="cestne-vyhlasenie-checkbox"
    >
      <IDSKCheckbox
        :id="cestneVyhlasenie.cestneVyhlasenieSpecId?.toString()"
        :checked="cestneVyhlasenie.potvrdeneZiadatelom ?? false"
        :model-value="cestneVyhlasenie.potvrdeneZiadatelom ?? false"
        readonly
        disabled
        is-in-checkboxes
      >
        <template #default>
          <div v-html="richTextToGovukHtml(cestneVyhlasenie.znenie)" />
        </template>
      </IDSKCheckbox>
    </div>
  </template>
  <div v-if="podpisovatelia">
    <IDSKHeading
      :as="cestneVyhlasenieEnabled ? 'h3' : 'h2'"
      :variant="cestneVyhlasenieEnabled ? 'm' : 'l'"
    >
      {{ podpisovateliaHeading }}
    </IDSKHeading>

    <!-- toto som slicol pretoze pre FO v cestnom prehlaseni sa nastavuje len jeden podpisovatel -->

    <DisplayList
      v-for="(podpisovatel, idx) in podpisovatelia.slice(
        0,
        isFo ? 1 : podpisovatelia.length
      )"
      :key="podpisovatel"
      bold="title"
      variant="stacked"
      full-width
    >
      <dt>{{ t('zoppm.zhrnutie.cestneVyhlasenie.osobaCislo') }} {{ idx + 1 }}</dt>
      <dd>
        {{ podpisovatel?.menoUplne }}<br />

        <Address :data="podpisovatel.adresaUplna" />
      </dd>
    </DisplayList>
  </div>

  <div v-if="!stavPodana" class="d-flex align-items-right">
    <IDSKButton
      variant="secondary"
      type="button"
      class="mb-0"
      :data-test-id="`zoppm-cestne-vyhlasenie-edit`"
      @click="
        () =>
          steps.setStep(NazvyKrokov['cestneVyhlasenie'], {
            ref: NazvyKrokov['zhrnutie'],
          })
      "
    >
      {{ t('zoppm.zhrnutie.editBtn') }}
    </IDSKButton>
  </div>

  <hr class="mb-8 mt-8" />
</template>

<style lang="scss">
.cestne-vyhlasenie-checkbox {
  & .govuk-checkboxes__label {
    opacity: 1 !important;
  }
  & .govuk-checkboxes__label::before {
    opacity: 0.5 !important;
  }
}
</style>
